import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { DiscordIcon } from './Icons';
import type { SessionUser } from '../types';
import { HALLOWEEN_ENABLED } from '../config/halloween';

interface NavbarProps { user: SessionUser | null; loading: boolean; }

export function Navbar({ user, loading }: NavbarProps) {
  const location = useLocation();
  const invite = import.meta.env.VITE_DISCORD_INVITE_URL || 'https://discord.gg/hAqvebcSh3';
  const [liveInfo, setLiveInfo] = useState<{ isLive: boolean; url?: string; id?: string } | null>(null);
  const [activeSection, setActiveSection] = useState<'home' | 'episodes' | 'news' | 'legends' | ''>('home');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const hostname = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';
  const isSubdomain = hostname.startsWith('apply.') || hostname.startsWith('rules.');
  const mainHost = 'https://ugeappagesmp.online';

  // Live stream status polling
  useEffect(() => {
    const checkLive = () => {
      fetch('/api/live')
        .then((r) => r.json())
        .then((data) => setLiveInfo(data?.isLive ? data : null))
        .catch(() => setLiveInfo(null));
    };
    checkLive();
    const interval = setInterval(checkLive, 45_000);
    return () => clearInterval(interval);
  }, []);

  // Active section tracking (supports both URL hash and scroll position on home page)
  useEffect(() => {
    if (location.pathname !== '/') {
      setActiveSection('');
      return;
    }

    const updateFromHash = () => {
      const hash = window.location.hash;
      if (hash === '#episodes') {
        setActiveSection('episodes');
      } else if (hash === '#news') {
        setActiveSection('news');
      } else if (hash === '#legends') {
        setActiveSection('legends');
      } else if (!hash || window.scrollY < 300) {
        setActiveSection('home');
      }
    };

    updateFromHash();
    window.addEventListener('hashchange', updateFromHash);

    // Scroll spy for smooth scrolling on homepage
    const handleScroll = () => {
      const episodesEl = document.getElementById('episodes');
      const legendsEl = document.getElementById('legends');
      const scrollY = window.scrollY;

      if (legendsEl) {
        const top = legendsEl.getBoundingClientRect().top + scrollY - 240;
        if (scrollY >= top) {
          setActiveSection('legends');
          return;
        }
      }

      if (episodesEl) {
        const top = episodesEl.getBoundingClientRect().top + scrollY - 240;
        const bottom = top + episodesEl.offsetHeight;
        if (scrollY >= top && scrollY < bottom) {
          setActiveSection('episodes');
          return;
        }
      }

      if (scrollY < 400) {
        setActiveSection('home');
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    // If page is loaded with hash (#episodes, #legends), scroll to it after mount
    if (window.location.hash) {
      const hashId = window.location.hash.replace('#', '');
      setTimeout(() => {
        const el = document.getElementById(hashId);
        if (el) {
          const lenisInstance = (window as any).__lenis;
          if (lenisInstance && typeof lenisInstance.scrollTo === 'function') {
            lenisInstance.scrollTo(el, { offset: -80, duration: 1.2 });
          } else {
            const top = el.getBoundingClientRect().top + window.scrollY - 80;
            window.scrollTo({ top, behavior: 'smooth' });
          }
        }
      }, 500);
    }

    return () => {
      window.removeEventListener('hashchange', updateFromHash);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [location.pathname]);

  const scrollToHash = (e: React.MouseEvent, targetId: string) => {
    if (location.pathname === '/' && !isSubdomain) {
      const el = document.getElementById(targetId);
      if (el) {
        e.preventDefault();
        window.history.pushState(null, '', `#${targetId}`);
        setActiveSection(targetId as 'episodes' | 'legends');

        const lenisInstance = (window as any).__lenis;
        if (lenisInstance && typeof lenisInstance.scrollTo === 'function') {
          lenisInstance.scrollTo(el, { offset: -80, duration: 1.2 });
        } else {
          const top = el.getBoundingClientRect().top + window.scrollY - 80;
          window.scrollTo({ top, behavior: 'smooth' });
        }
      }
    }
  };

  const isHomeActive = location.pathname === '/' && activeSection === 'home';
  const isEpisodesActive = location.pathname === '/' && activeSection === 'episodes';
  const isNewsActive = location.pathname.startsWith('/news');
  const isEventsActive = location.pathname.startsWith('/events');
  const isLegendsActive = location.pathname === '/' && activeSection === 'legends';
  const isJourneyActive = location.pathname === '/journey';
  const isEconomyActive = location.pathname === '/economy';
  const isRulesActive = location.pathname === '/rules' || hostname.startsWith('rules.');
  const isProfileActive = location.pathname === '/profile';

  return (
    <header className="nav-wrap">
      <nav className="nav-shell" aria-label="Primary navigation">
        {isSubdomain ? (
          <a href={`${mainHost}/`} className="brand" aria-label="Uge Appage SMP home">
            <img src="/assets/ugeappage-logo-zoomed.webp" className="brand-icon" alt="Uge Appage SMP" />
            <span className="brand-wordmark"><b>UGE APPAGE SMP{HALLOWEEN_ENABLED ? <span className="hl-brand-pumpkin">🎃</span> : null}</b><small>SEASON III</small></span>
          </a>
        ) : (
          <Link to="/" className="brand" aria-label="Uge Appage SMP home">
            <img src="/assets/ugeappage-logo-zoomed.webp" className="brand-icon" alt="Uge Appage SMP" />
            <span className="brand-wordmark"><b>UGE APPAGE SMP{HALLOWEEN_ENABLED ? <span className="hl-brand-pumpkin">🎃</span> : null}</b><small>SEASON III</small></span>
          </Link>
        )}

        <div className="nav-links">
          {isSubdomain ? (
            <>
              <a href={`${mainHost}/`} className={isHomeActive ? 'active' : ''}>Home</a>
              <a href={`${mainHost}/journey`} className={isJourneyActive ? 'active' : ''}>Our Journey</a>
              <a href={`${mainHost}/#episodes`} className={isEpisodesActive ? 'active' : ''}>Episodes</a>
              <a href={`${mainHost}/events`} className={isEventsActive ? 'active' : ''}>Events</a>
              <a href={`${mainHost}/news`} className={isNewsActive ? 'active' : ''}>News</a>
              <a href={`${mainHost}/economy`} className={isEconomyActive ? 'active' : ''}>Economy</a>
              <a href="https://rules.ugeappagesmp.online/" className={isRulesActive ? 'active' : ''}>Rules</a>
              {user && <a href={`${mainHost}/profile`} className={isProfileActive ? 'active' : ''}>My Dossier</a>}
              <a href={`${mainHost}/#legends`} className={isLegendsActive ? 'active' : ''}>Hall of Legends</a>
            </>
          ) : (
            <>
              <Link to="/" className={isHomeActive ? 'active' : ''}>Home</Link>
              <Link to="/journey" className={isJourneyActive ? 'active' : ''}>Our Journey</Link>
              <a
                href="/#episodes"
                className={isEpisodesActive ? 'active' : ''}
                onClick={(e) => scrollToHash(e, 'episodes')}
              >
                Episodes
              </a>
              <Link to="/events" className={isEventsActive ? 'active' : ''}>Events</Link>
              <Link to="/news" className={isNewsActive ? 'active' : ''}>News</Link>
              <Link to="/economy" className={isEconomyActive ? 'active' : ''}>Economy</Link>
              <a href="https://rules.ugeappagesmp.online/" className={isRulesActive ? 'active' : ''}>Rules</a>
              {user && <Link to="/profile" className={isProfileActive ? 'active' : ''}>My Dossier</Link>}
              <a
                href="/#legends"
                className={isLegendsActive ? 'active' : ''}
                onClick={(e) => scrollToHash(e, 'legends')}
              >
                Hall of Legends
              </a>
            </>
          )}
        </div>

        <div className="nav-actions">
          {/* Active YouTube Live Button (Shown ONLY when a stream is genuinely live) */}
          {liveInfo?.isLive && (
            <a
              className="live-header-btn"
              href={liveInfo.url || `https://www.youtube.com/watch?v=${liveInfo.id}`}
              target="_blank"
              rel="noreferrer"
              title="A YouTube Live broadcast is happening right now!"
            >
              <span className="live-header-dot" />
              <span>Watch YouTube Live</span>
            </a>
          )}

          <a className="discord-btn compact" href={invite} target="_blank" rel="noreferrer"><DiscordIcon /><span>Join Discord</span><i /></a>
          {!loading && (user ? (
            isSubdomain ? (
              <a href={`${mainHost}/profile`} className={`profile-pill ${isProfileActive ? 'active' : ''}`} title={`${user.displayName} — Open Citizen Dossier & Financial Ledger`}>
                <span className="avatar-wrap"><img src={user.avatarUrl || '/assets/ugeappage-logo-zoomed.webp'} alt="" /><em><DiscordIcon size={10} /></em></span>
                <span><b>{user.displayName}</b><small className={user.status || ''}>{user.status === 'passed' ? '✓ WHITELISTED' : user.status === 'pending' ? '⌛ PENDING' : 'APPLICANT'}</small></span>
              </a>
            ) : (
              <Link to="/profile" className={`profile-pill ${isProfileActive ? 'active' : ''}`} title={`${user.displayName} — Open Citizen Dossier & Financial Ledger`}>
                <span className="avatar-wrap"><img src={user.avatarUrl || '/assets/ugeappage-logo-zoomed.webp'} alt="" /><em><DiscordIcon size={10} /></em></span>
                <span><b>{user.displayName}</b><small className={user.status || ''}>{user.status === 'passed' ? '✓ WHITELISTED' : user.status === 'pending' ? '⌛ PENDING' : 'APPLICANT'}</small></span>
              </Link>
            )
          ) : (
            <a className="login-btn" href="/auth/discord"><DiscordIcon /><span>Login</span></a>
          ))}

          {/* Mobile & Tablet Hamburger Toggle */}
          <button
            type="button"
            className={`nav-mobile-toggle ${mobileMenuOpen ? 'open' : ''}`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? 'Close Navigation Menu' : 'Open Navigation Menu'}
            aria-expanded={mobileMenuOpen}
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </nav>

      {/* Mobile Drawer Menu (Visible on mobile/tablet when open) */}
      <div className={`nav-mobile-drawer ${mobileMenuOpen ? 'open' : ''}`} onClick={() => setMobileMenuOpen(false)}>
        <div className="nav-mobile-drawer-inner" onClick={(e) => e.stopPropagation()}>
          <div className="nav-mobile-drawer-header">
            <span className="brand-wordmark"><b>UGE APPAGE SMP{HALLOWEEN_ENABLED ? <span className="hl-brand-pumpkin">🎃</span> : null}</b><small>SEASON III NAVIGATION</small></span>
            <button
              type="button"
              className="drawer-close-btn"
              onClick={() => setMobileMenuOpen(false)}
              aria-label="Close Menu"
            >
              ✕
            </button>
          </div>

          <div className="nav-mobile-drawer-links">
            {isSubdomain ? (
              <>
                <a href={`${mainHost}/`} className={isHomeActive ? 'active' : ''} onClick={() => setMobileMenuOpen(false)}>Home</a>
                <a href={`${mainHost}/journey`} className={isJourneyActive ? 'active' : ''} onClick={() => setMobileMenuOpen(false)}>Our Journey</a>
                <a href={`${mainHost}/#episodes`} className={isEpisodesActive ? 'active' : ''} onClick={() => setMobileMenuOpen(false)}>Episodes</a>
                <a href={`${mainHost}/events`} className={isEventsActive ? 'active' : ''} onClick={() => setMobileMenuOpen(false)}>Events</a>
                <a href={`${mainHost}/news`} className={isNewsActive ? 'active' : ''} onClick={() => setMobileMenuOpen(false)}>News</a>
                <a href={`${mainHost}/economy`} className={isEconomyActive ? 'active' : ''} onClick={() => setMobileMenuOpen(false)}>Economy</a>
                <a href="https://rules.ugeappagesmp.online/" className={isRulesActive ? 'active' : ''} onClick={() => setMobileMenuOpen(false)}>Rules</a>
                {user && <a href={`${mainHost}/profile`} className={isProfileActive ? 'active' : ''} onClick={() => setMobileMenuOpen(false)}>My Citizen Dossier</a>}
                <a href={`${mainHost}/#legends`} className={isLegendsActive ? 'active' : ''} onClick={() => setMobileMenuOpen(false)}>Hall of Legends</a>
              </>
            ) : (
              <>
                <Link to="/" className={isHomeActive ? 'active' : ''} onClick={() => setMobileMenuOpen(false)}>Home</Link>
                <Link to="/journey" className={isJourneyActive ? 'active' : ''} onClick={() => setMobileMenuOpen(false)}>Our Journey</Link>
                <a
                  href="/#episodes"
                  className={isEpisodesActive ? 'active' : ''}
                  onClick={(e) => {
                    setMobileMenuOpen(false);
                    scrollToHash(e, 'episodes');
                  }}
                >
                  Episodes
                </a>
                <Link to="/events" className={isEventsActive ? 'active' : ''} onClick={() => setMobileMenuOpen(false)}>Events</Link>
                <Link to="/news" className={isNewsActive ? 'active' : ''} onClick={() => setMobileMenuOpen(false)}>News</Link>
                <Link to="/economy" className={isEconomyActive ? 'active' : ''} onClick={() => setMobileMenuOpen(false)}>Economy</Link>
                <a href="https://rules.ugeappagesmp.online/" className={isRulesActive ? 'active' : ''} onClick={() => setMobileMenuOpen(false)}>Rules</a>
                {user && <Link to="/profile" className={isProfileActive ? 'active' : ''} onClick={() => setMobileMenuOpen(false)}>My Citizen Dossier</Link>}
                <a
                  href="/#legends"
                  className={isLegendsActive ? 'active' : ''}
                  onClick={(e) => {
                    setMobileMenuOpen(false);
                    scrollToHash(e, 'legends');
                  }}
                >
                  Hall of Legends
                </a>
              </>
            )}
          </div>

          <div className="nav-mobile-drawer-footer">
            <a className="primary-btn enchant full-width-btn" href="https://apply.ugeappagesmp.online" onClick={() => setMobileMenuOpen(false)}>
              ✦ Apply For Whitelist
            </a>
            <a className="discord-btn full-width-btn" href={invite} target="_blank" rel="noreferrer">
              <DiscordIcon /> Join Official Discord
            </a>
          </div>
        </div>
      </div>
    </header>
  );
}
